import React from 'react';
import { BookOpen, CalendarDays, CheckCheck, ChevronDown, CircleCheck, Database, FilePen, FilePlus2, FileSearch, FileText, Layers, Search, ShieldCheck } from 'lucide-react';
import { RAG_THINKING_LABEL, RagThinkingIndicator } from './RagChrome';

export const appendRagPlannerStep = (message, step) => {
    const currentSteps = Array.isArray(message.metadata?.plannerSteps) ? message.metadata.plannerSteps : [];
    const nextStep = {
        id: step.id || `${Date.now()}-${currentSteps.length}`,
        type: step.type || 'status',
        message: step.message || '',
        steps: Array.isArray(step.steps) ? step.steps : [],
        mode: step.mode || null,
    };
    return {
        ...message,
        metadata: {
            ...(message.metadata || {}),
            plannerSteps: [...currentSteps, nextStep].slice(-28),
        },
    };
};

export const mergeJaneRunPlanProgress = (previousPlan = null, incomingPlan = null) => {
    if (!incomingPlan || typeof incomingPlan !== 'object') return previousPlan;
    const incomingSteps = Array.isArray(incomingPlan.steps) ? incomingPlan.steps.filter(Boolean) : [];
    const completeStatuses = new Set(['complete', 'completed', 'done', 'success', 'succeeded']);
    const failureStatuses = new Set(['failed', 'failure', 'error', 'skipped', 'cancelled', 'canceled']);
    const runningStatuses = new Set(['running', 'active', 'in_progress']);
    const incomingCompletesRun = completeStatuses.has(String(incomingPlan.status || '').trim().toLowerCase());
    const furthestIncomingProgressIndex = incomingSteps.reduce((furthestIndex, step, index) => {
        const status = String(step?.status || '').trim().toLowerCase();
        return runningStatuses.has(status) || completeStatuses.has(status) || failureStatuses.has(status)
            ? index
            : furthestIndex;
    }, -1);
    const mergeStepProgress = (incomingStep, index, previousStep = null) => {
        const mergedStep = previousStep ? { ...previousStep, ...incomingStep } : incomingStep;
        const previousStatus = String(previousStep?.status || '').trim().toLowerCase();
        const incomingStatus = String(incomingStep?.status || '').trim().toLowerCase();
        const failureStatus = failureStatuses.has(incomingStatus)
            ? incomingStatus
            : (failureStatuses.has(previousStatus) ? previousStatus : '');

        if (incomingCompletesRun) {
            return { ...mergedStep, status: failureStatus || 'complete' };
        }
        if (failureStatus) return { ...mergedStep, status: failureStatus };
        if (completeStatuses.has(previousStatus)) return { ...mergedStep, status: 'complete' };
        if (index < furthestIncomingProgressIndex && (runningStatuses.has(previousStatus) || runningStatuses.has(incomingStatus))) {
            return { ...mergedStep, status: 'complete' };
        }
        if (runningStatuses.has(previousStatus) && !runningStatuses.has(incomingStatus) && !completeStatuses.has(incomingStatus)) {
            return { ...mergedStep, status: previousStep.status };
        }
        return mergedStep;
    };
    const normalizedIncomingSteps = incomingSteps.map((step, index) => mergeStepProgress(step, index));
    const normalizedIncomingPlan = Array.isArray(incomingPlan.steps)
        ? { ...incomingPlan, steps: normalizedIncomingSteps }
        : incomingPlan;

    if (!previousPlan || typeof previousPlan !== 'object') return normalizedIncomingPlan;
    const previousId = String(previousPlan.id || previousPlan.run_id || '');
    const incomingId = String(incomingPlan.id || incomingPlan.run_id || '');
    if (previousId && incomingId && previousId !== incomingId) return normalizedIncomingPlan;

    const previousSteps = Array.isArray(previousPlan.steps) ? previousPlan.steps.filter(Boolean) : [];
    if (incomingSteps.length === 0) {
        return {
            ...previousPlan,
            ...incomingPlan,
            steps: incomingCompletesRun
                ? previousSteps.map((step) => {
                    const status = String(step?.status || '').trim().toLowerCase();
                    return { ...step, status: failureStatuses.has(status) ? status : 'complete' };
                })
                : previousSteps,
        };
    }

    const mergedSteps = previousSteps.map((step) => {
        const matchingStepIndex = incomingSteps.findIndex((candidate) => (
            String(candidate?.id || '') !== ''
            && String(candidate.id) === String(step?.id || '')
        ));
        if (matchingStepIndex >= 0) {
            return mergeStepProgress(incomingSteps[matchingStepIndex], matchingStepIndex, step);
        }
        const priorStatus = String(step?.status || '').trim().toLowerCase();
        if (incomingCompletesRun) {
            return { ...step, status: failureStatuses.has(priorStatus) ? priorStatus : 'complete' };
        }
        if (runningStatuses.has(priorStatus)) {
            return { ...step, status: 'complete' };
        }
        return step;
    });
    normalizedIncomingSteps.forEach((step) => {
        const stepId = String(step?.id || '');
        if (!stepId || !mergedSteps.some((candidate) => String(candidate?.id || '') === stepId)) {
            mergedSteps.push(step);
        }
    });

    return {
        ...previousPlan,
        ...incomingPlan,
        steps: mergedSteps.slice(-28),
    };
};

export const shouldCollapseJaneRunPlanByDefault = ({
    isLiveAssistantThinking = false,
    isCompletedAssistantAnswer = false,
    isPendingWithoutAnswer = false,
    runPlanStatus = '',
} = {}) => !isLiveAssistantThinking && (
    isCompletedAssistantAnswer
    || (isPendingWithoutAnswer && String(runPlanStatus).trim().toLowerCase() === 'complete')
);

const RAG_PLANNER_PLACEHOLDER_VALUES = new Set(['n/a', 'na', 'none', 'null', 'unknown', '-', '—', 'tbd']);

const stringifyRagPlannerValue = (value) => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string') {
        const trimmed = value.trim();
        return RAG_PLANNER_PLACEHOLDER_VALUES.has(trimmed.toLowerCase()) ? '' : trimmed;
    }
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    if (typeof value === 'object') {
        const directValue = value.label ?? value.name ?? value.title ?? value.message ?? value.text ?? value.value ?? value.id;
        if (directValue !== null && directValue !== undefined) return stringifyRagPlannerValue(directValue);
        try {
            return JSON.stringify(value);
        } catch (error) {
            return '';
        }
    }
    return String(value || '').trim();
};

const normalizeRagPlannerValues = (values) => {
    const rawValues = Array.isArray(values) ? values : (values === null || values === undefined ? [] : [values]);
    return rawValues.flatMap((value) => (
        Array.isArray(value)
            ? normalizeRagPlannerValues(value)
            : [stringifyRagPlannerValue(value)]
    )).filter(Boolean);
};

const normalizeRagPlannerGroup = (group, fallbackLabel = '') => {
    if (group === null || group === undefined) return null;
    if (typeof group !== 'object' || Array.isArray(group)) {
        const values = normalizeRagPlannerValues(group);
        return values.length > 0 ? { label: fallbackLabel, values } : null;
    }

    const label = stringifyRagPlannerValue(group.label ?? group.name ?? group.title ?? group.key ?? fallbackLabel);
    let values = normalizeRagPlannerValues(group.values ?? group.items ?? group.findings ?? group.details ?? group.results ?? group.sources ?? group.text ?? group.value);
    if (values.length === 0 && group.message !== undefined) {
        values = normalizeRagPlannerValues(group.message);
    }
    if (label === '' && values.length === 0) return null;
    return { label, values };
};

export const normalizeRagPlannerSteps = (plannerSteps = []) => {
    if (!Array.isArray(plannerSteps)) return [];

    return plannerSteps.flatMap((step, stepIndex) => {
        if (step === null || step === undefined) return [];
        if (typeof step !== 'object') {
            const message = stringifyRagPlannerValue(step);
            return message !== '' ? [{ id: `${stepIndex}`, type: 'status', message, steps: [], mode: null }] : [];
        }

        const nestedSource = Array.isArray(step.steps)
            ? step.steps
            : (Array.isArray(step.groups) ? step.groups : (Array.isArray(step.items) ? step.items : []));
        const nestedGroups = nestedSource.map((group) => normalizeRagPlannerGroup(group)).filter(Boolean);

        const directGroups = nestedGroups.length > 0
            ? nestedGroups
            : [normalizeRagPlannerGroup({
                label: step.label ?? step.name ?? step.title ?? '',
                values: step.values ?? step.findings ?? step.details ?? step.results ?? step.sources ?? step.value,
            })].filter(Boolean);

        const message = stringifyRagPlannerValue(step.message ?? step.status ?? step.text ?? '');
        if (message === '' && directGroups.length === 0) return [];

        return [{
            id: step.id || `${stepIndex}`,
            type: step.type || 'status',
            message,
            steps: directGroups,
            mode: step.mode || null,
        }];
    });
};

const addRagPlannerGroup = (groups, label, values) => {
    const group = normalizeRagPlannerGroup({ label, values });
    if (group && group.values.length > 0) groups.push(group);
};

export const buildRagPlannerStepsFromIntelligence = (intelligence = null) => {
    if (!intelligence || typeof intelligence !== 'object') return [];
    const providedPlannerSteps = Array.isArray(intelligence.plannerSteps) && normalizeRagPlannerSteps(intelligence.plannerSteps).length > 0
        ? intelligence.plannerSteps
        : [];

    const plan = intelligence.plan && typeof intelligence.plan === 'object' ? intelligence.plan : null;
    const trace = intelligence.route_trace && typeof intelligence.route_trace === 'object' ? intelligence.route_trace : null;
    const groups = [];

    if (plan) {
        addRagPlannerGroup(groups, 'Goal', plan.runtime_route ?? plan.route_goal);
        addRagPlannerGroup(groups, 'Plan', plan.planner_tasks);
        addRagPlannerGroup(groups, 'Search terms', plan.queries);
        addRagPlannerGroup(groups, 'Document types', plan.document_types);
        addRagPlannerGroup(groups, 'Legal phrases', plan.legal_phrases);
        addRagPlannerGroup(groups, 'Case types', plan.case_types);
        addRagPlannerGroup(groups, 'Names', plan.names);
        addRagPlannerGroup(groups, 'Signals', plan.signals);
    }

    if (trace) {
        addRagPlannerGroup(groups, 'Route', trace.route);
        addRagPlannerGroup(groups, 'Tools', trace.tools);
        addRagPlannerGroup(groups, 'Template', trace.prompt_template);
        if (trace.evidence?.retrieval_mode) addRagPlannerGroup(groups, 'Evidence', trace.evidence.retrieval_mode);
    }

    const message = stringifyRagPlannerValue(plan?.intent ?? trace?.route_reason ?? intelligence.summary ?? '');
    if (message === '' && groups.length === 0) return providedPlannerSteps;
    const intelligencePlanStep = {
        id: 'intelligence-plan',
        type: 'planner',
        message: message || 'I prepared route context.',
        steps: groups,
        mode: 'deterministic',
    };
    const preservedPlannerSteps = providedPlannerSteps.filter((step) => (
        !step
        || typeof step !== 'object'
        || String(step.id || '') !== intelligencePlanStep.id
    ));
    return [...preservedPlannerSteps, intelligencePlanStep];
};

export const getRagPlannerStepsFromMessage = (message, intelligenceOverride = null) => {
    const metadata = message?.metadata || {};
    const intelligence = intelligenceOverride || metadata.intelligence || null;
    const directSteps = Array.isArray(metadata.plannerSteps) ? metadata.plannerSteps : [];
    const normalizedDirectSteps = normalizeRagPlannerSteps(directSteps);
    const isCompletedAnswer = Boolean(message?.answer && !metadata.streaming && !message?.pending);
    const intelligenceHasChecklist = Array.isArray(intelligence?.plannerSteps)
        && normalizeRagPlannerSteps(intelligence.plannerSteps).length > 0;
    const intelligenceForDisplay = isCompletedAnswer && normalizedDirectSteps.length > 0 && !intelligenceHasChecklist
        ? { ...(intelligence || {}), plannerSteps: directSteps }
        : intelligence;
    const intelligenceSteps = buildRagPlannerStepsFromIntelligence(intelligenceForDisplay);
    if (isCompletedAnswer && normalizeRagPlannerSteps(intelligenceSteps).length > 0) {
        return intelligenceSteps;
    }
    if (normalizedDirectSteps.length > 0) return directSteps;
    return intelligenceSteps;
};

export const countRagPlannerItems = (plannerSteps = []) => normalizeRagPlannerSteps(plannerSteps).reduce((count, step) => {
    const valueCount = (step.steps || []).reduce((groupCount, group) => groupCount + Math.max(1, (group.values || []).length), 0);
    return count + Math.max(1, valueCount);
}, 0);

const normalizeJaneRunStepStatus = (status = '', fallback = 'pending') => {
    const normalized = String(status || '').trim().toLowerCase().replace(/[\s_-]+/g, '_');
    if (['complete', 'completed', 'done', 'success', 'succeeded'].includes(normalized)) return 'complete';
    if (['running', 'active', 'working', 'streaming', 'in_progress'].includes(normalized)) return 'running';
    if (['failed', 'error', 'blocked'].includes(normalized)) return 'failed';
    if (['skipped', 'cancelled', 'canceled'].includes(normalized)) return 'skipped';
    if (['pending', 'queued', 'waiting'].includes(normalized)) return 'pending';
    return fallback;
};

const isTransientFinalResponseStep = (step = {}) => {
    const text = [step.label, step.detail, ...(Array.isArray(step.meta) ? step.meta : [])]
        .map(value => stringifyRagPlannerValue(value).toLowerCase())
        .filter(Boolean)
        .join(' ');
    return /\b(?:drafting|preparing)\s+(?:the\s+)?final\s+(?:answer|response)\b/.test(text);
};

const normalizeJaneRunStep = (step, index = 0) => {
    if (step === null || step === undefined) return null;
    if (typeof step !== 'object' || Array.isArray(step)) {
        const label = stringifyRagPlannerValue(step);
        return label ? { id: `step-${index}`, label, detail: '', status: 'pending', meta: [] } : null;
    }

    const label = stringifyRagPlannerValue(step.label ?? step.title ?? step.name ?? step.message ?? `Step ${index + 1}`);
    const detail = stringifyRagPlannerValue(step.detail ?? step.description ?? step.status_message ?? step.text ?? '');
    const meta = normalizeRagPlannerValues(step.meta ?? step.values ?? step.items ?? step.sources ?? step.details);
    if (label === '' && detail === '' && meta.length === 0) return null;
    return {
        id: stringifyRagPlannerValue(step.id ?? step.key) || `step-${index}`,
        label: label || `Step ${index + 1}`,
        detail,
        status: normalizeJaneRunStepStatus(step.status, 'pending'),
        meta,
    };
};

const janeRunStepFromPlannerGroup = (group, index, status = 'pending') => {
    const label = stringifyRagPlannerValue(group?.label || `Step ${index + 1}`);
    const values = normalizeRagPlannerValues(group?.values || []);
    return {
        id: `planner-group-${index}`,
        label: label || `Step ${index + 1}`,
        detail: values.slice(0, 2).join(' · '),
        status,
        meta: values.slice(2, 8),
    };
};

const janeRunStepsFromPlannerStep = (step, index, status = 'running') => {
    const message = stringifyRagPlannerValue(step?.message ?? '');
    const groups = Array.isArray(step?.steps) ? step.steps : [];
    const taskGroup = groups.find(group => /^(?:plan|tasks?|steps?)$/i.test(stringifyRagPlannerValue(group?.label || '')));
    const taskValues = normalizeRagPlannerValues(taskGroup?.values || []);
    if (taskValues.length > 0) {
        return taskValues.slice(0, 8).map((value, valueIndex) => ({
            id: `planner-task-${step?.id || index}-${valueIndex}`,
            label: value,
            detail: '',
            status,
            meta: [],
        }));
    }
    if (message !== '') {
        const detailValues = groups.flatMap((group) => {
            const label = stringifyRagPlannerValue(group?.label || '');
            const values = normalizeRagPlannerValues(group?.values || []);
            if (label && values.length > 0) return [`${label}: ${values.slice(0, 3).join(', ')}`];
            if (values.length > 0) return [values.slice(0, 3).join(', ')];
            return label ? [label] : [];
        });
        return [{
            id: `planner-step-${step?.id || index}`,
            label: message,
            detail: detailValues.slice(0, 2).join(' · '),
            status,
            meta: detailValues.slice(2, 6),
        }];
    }
    return groups.map((group, groupIndex) => janeRunStepFromPlannerGroup(group, index + groupIndex, status));
};

export const normalizeJaneRunPlan = (plan = null, fallbackPlannerSteps = [], options = {}) => {
    const pending = Boolean(options.pending);
    const streaming = Boolean(options.streaming);
    const complete = Boolean(options.complete);
    const failed = Boolean(options.failed);
    const liveTraceOnly = Boolean(options.liveTraceOnly);
    const directPlan = plan && typeof plan === 'object' ? plan : null;
    const directSteps = Array.isArray(directPlan?.steps)
        ? directPlan.steps.map(normalizeJaneRunStep).filter(Boolean)
        : [];

    const plannerSteps = normalizeRagPlannerSteps(fallbackPlannerSteps);
    const plannerGroups = plannerSteps.flatMap(step => Array.isArray(step.steps) ? step.steps : []);
    const livePlannerSteps = plannerSteps.flatMap((step, index) => (
        janeRunStepsFromPlannerStep(step, index, pending || streaming ? 'running' : 'complete')
    ));
    // A supplied run plan is authoritative for a long-running/background tool.
    // The live routing trace only becomes the plan when no tool-specific steps
    // exist; otherwise generic entries such as "Goal: case_trial_analysis"
    // replace the actual document, synthesis, and verification progress.
    const derivedSteps = directSteps.length > 0
        ? directSteps
        : (livePlannerSteps.length > 0
            ? livePlannerSteps
            : plannerGroups.map((group, index) => janeRunStepFromPlannerGroup(group, index, index === 0 ? 'complete' : 'pending')));

    if (derivedSteps.length === 0 && !directPlan) return null;

    const rawRunStatus = normalizeJaneRunStepStatus(
        directPlan?.status || (failed ? 'failed' : (complete ? 'complete' : (pending || streaming ? 'running' : 'pending'))),
        pending || streaming ? 'running' : (complete ? 'complete' : 'pending')
    );
    const runStatus = complete && !['failed', 'skipped'].includes(rawRunStatus) ? 'complete' : rawRunStatus;
    const baseSteps = derivedSteps.length > 0 ? derivedSteps : [
        { id: 'understand', label: 'Understand request', detail: '', status: runStatus === 'pending' ? 'running' : 'complete', meta: [] },
    ];
    const visibleBaseSteps = runStatus === 'complete'
        ? baseSteps.filter(step => !isTransientFinalResponseStep(step))
        : baseSteps;
    // Only show work reported by the route or planner. A synthetic delivery
    // step repeats the existence of the chat response and appears all at once
    // for background runs, which makes it look like work that never streamed.
    const steps = visibleBaseSteps;
    const hasRunningStep = steps.some(step => step.status === 'running');
    const currentStepId = stringifyRagPlannerValue(directPlan?.current_step_id ?? directPlan?.currentStepId ?? '');
    const currentStepIndex = currentStepId
        ? steps.findIndex(step => String(step?.id || '') === currentStepId)
        : -1;
    const explicitFailureStepIndex = steps.findIndex(step => step.status === 'failed');
    const runningStepIndex = steps.findIndex(step => step.status === 'running');
    const terminalFailureStepIndex = currentStepIndex >= 0
        ? currentStepIndex
        : (explicitFailureStepIndex >= 0
            ? explicitFailureStepIndex
            : (runningStepIndex >= 0 ? runningStepIndex : steps.length - 1));
    const finalSteps = steps.map((step, index) => {
        if (runStatus === 'complete') return { ...step, status: step.status === 'failed' ? 'failed' : 'complete' };
        if (runStatus === 'failed') {
            if (index === terminalFailureStepIndex) return { ...step, status: 'failed' };
            return step.status === 'running' ? { ...step, status: 'pending' } : step;
        }
        if (!hasRunningStep && index === 0 && (pending || streaming)) return { ...step, status: 'running' };
        return step;
    });

    const title = stringifyRagPlannerValue(directPlan?.title ?? directPlan?.name ?? options.title ?? 'Jane run');
    const summary = stringifyRagPlannerValue(directPlan?.summary ?? directPlan?.message ?? plannerSteps[plannerSteps.length - 1]?.message ?? options.summary ?? '');
    const mode = stringifyRagPlannerValue(directPlan?.mode ?? plannerSteps[plannerSteps.length - 1]?.mode ?? options.mode ?? '');
    return {
        id: stringifyRagPlannerValue(directPlan?.id ?? directPlan?.run_id ?? options.id ?? ''),
        title,
        summary,
        mode,
        status: runStatus,
        steps: finalSteps,
        sourceCount: Number(directPlan?.source_count ?? options.sourceCount ?? 0) || 0,
        verification: directPlan?.verification && typeof directPlan.verification === 'object' ? directPlan.verification : null,
    };
};

export const getJaneRunPlanFromMessage = (message, intelligenceOverride = null, options = {}) => {
    const metadata = message?.metadata || {};
    const plannerSteps = getRagPlannerStepsFromMessage(message, intelligenceOverride);
    const finalRunPlan = intelligenceOverride?.run_plan || metadata.intelligence?.run_plan || null;
    const runPlan = metadata.runPlan || finalRunPlan || null;
    const runPlanStatus = normalizeJaneRunStepStatus(runPlan?.status || '', 'pending');
    const complete = Boolean(message?.answer && !metadata.streaming && !message?.pending && (!runPlan || runPlanStatus === 'complete'));
    return normalizeJaneRunPlan(runPlan, plannerSteps, {
        pending: Boolean(message?.pending || runPlanStatus === 'running'),
        streaming: Boolean(metadata.streaming),
        complete,
        liveTraceOnly: Boolean(options.liveTraceOnly),
        title: message?.answer?.summary || metadata.intelligence?.plan?.route_goal || 'Jane run',
        sourceCount: Array.isArray(message?.answer?.citations) ? message.answer.citations.length : 0,
    });
};

const normalizeJaneThoughtText = (step = {}, index = 0) => {
    const label = stringifyRagPlannerValue(step.label || '');
    const detail = stringifyRagPlannerValue(step.detail || '');
    const meta = normalizeRagPlannerValues(step.meta || []);
    const raw = [label, detail, ...meta].join(' ').replace(/\s+/g, ' ').trim();
    const normalized = raw.toLowerCase();
    const stepStatus = normalizeJaneRunStepStatus(step.status, 'pending');
    const isComplete = stepStatus === 'complete';
    const firstPersonLabel = label
        .replace(/^Jane is\b/i, 'I’m')
        .replace(/^Jane will\b/i, 'I’ll')
        .replace(/^Jane has\b/i, 'I’ve')
        .replace(/^Jane finished\b/i, 'I finished');

    if (!raw) return '';
    const discoveryStepLabels = {
        'load selected discovery files': ['Loaded selected discovery files', 'Loading selected discovery files', 'Will load selected discovery files'],
        'extract and preserve every request': ['Extracted and preserved every request', 'Extracting and preserving every request', 'Will extract and preserve every request'],
        'draft request-specific objections': ['Drafted request-specific objections', 'Drafting request-specific objections', 'Will draft request-specific objections'],
        'verify authority support and guardrails': ['Verified authority support and guardrails', 'Verifying authority support and guardrails', 'Will verify authority support and guardrails'],
        'prepare the attorney-review draft': ['Prepared the attorney-review draft', 'Preparing the attorney-review draft', 'Will prepare the attorney-review draft'],
    };
    const discoveryLabels = discoveryStepLabels[label.toLowerCase()];
    if (discoveryLabels) {
        return discoveryLabels[isComplete ? 0 : (stepStatus === 'pending' ? 2 : 1)];
    }
    if (/^inventory case documents\b/i.test(label)) {
        return isComplete ? 'Inventoried case documents' : (stepStatus === 'pending' ? 'I’ll inventory case documents' : 'I’m inventorying case documents');
    }
    if (/^analyze case documents\b/i.test(label)) {
        return isComplete ? 'Analyzed case documents' : (stepStatus === 'pending' ? 'I’ll analyze case documents' : 'I’m analyzing case documents');
    }
    if (/^build complete case analysis\b/i.test(label)) {
        return isComplete ? 'Built the complete case analysis' : (stepStatus === 'pending' ? 'I’ll build the complete case analysis' : 'I’m building the complete case analysis');
    }
    if (/^verify findings and source coverage\b/i.test(label)) {
        return isComplete ? 'Verified findings and source coverage' : (stepStatus === 'pending' ? 'I’ll verify findings and source coverage' : 'I’m verifying findings and source coverage');
    }
    if (/^deliver for attorney review\b/i.test(label)) {
        return isComplete ? 'Delivered the analysis for attorney review' : (stepStatus === 'pending' ? 'I’ll deliver the analysis for attorney review' : 'I’m delivering the analysis for attorney review');
    }
    if (isComplete && /^(?:i['’]m|i am|i['’]ll|i will)\b/i.test(firstPersonLabel)) {
        const completedLabel = firstPersonLabel
            .replace(/^I['’]m\s+(choosing|deciding|planning)\b/i, 'Selected')
            .replace(/^I am\s+(choosing|deciding|planning)\b/i, 'Selected')
            .replace(/^I['’]m\s+(searching|retrieving|gathering|reviewing|reading)\b/i, 'Reviewed')
            .replace(/^I am\s+(searching|retrieving|gathering|reviewing|reading)\b/i, 'Reviewed')
            .replace(/^I['’]m\s+(drafting|writing|generating|preparing|building)\b/i, 'Prepared')
            .replace(/^I am\s+(drafting|writing|generating|preparing|building)\b/i, 'Prepared')
            .replace(/^I['’]ll\s+/i, 'Planned to ')
            .replace(/^I will\s+/i, 'Planned to ');
        return completedLabel.length > 180 ? `${completedLabel.slice(0, 177).trim()}...` : completedLabel;
    }
    if (/^(?:i['’]m|i am|i['’]ll|i will|i['’]ve|i have|i finished)\b/i.test(firstPersonLabel)) {
        return firstPersonLabel.length > 180 ? `${firstPersonLabel.slice(0, 177).trim()}...` : firstPersonLabel;
    }
    // Detail and meta are rendered directly beneath the thought headline. Keep
    // them available for classification, but do not fold them into the
    // headline too or Route/Tools (and similar metadata) appears twice.
    const cleaned = (label || raw)
        .replace(/^status:\s*/i, '')
        .replace(/^planner:\s*/i, '')
        .replace(/_/g, ' ')
        .trim();
    if (/planning retrieval|planned the retrieval|route|goal|determin|classif|decid/.test(normalized)) {
        return cleaned && !/^(?:route|goal)$/i.test(cleaned)
            ? `${isComplete ? 'Workflow selected' : 'I’m deciding the workflow'}: ${cleaned.length > 130 ? `${cleaned.slice(0, 127).trim()}...` : cleaned}`
            : (isComplete ? 'Workflow selected.' : 'I’m deciding what this request needs before answering.');
    }
    if (/case context|case name|case id|matching case|resolve|ground/.test(normalized)) {
        return cleaned ? `${isComplete ? 'Grounded the context' : 'I’m grounding the context'}: ${cleaned.length > 130 ? `${cleaned.slice(0, 127).trim()}...` : cleaned}` : (isComplete ? 'Grounded this in the right CaseDB case and selected sources.' : 'I’m grounding this in the right CaseDB case and selected sources.');
    }
    if (/permission|access|authorized|source metadata/.test(normalized)) {
        return cleaned ? `${isComplete ? 'Checked source access' : 'I’m checking source access'}: ${cleaned.length > 130 ? `${cleaned.slice(0, 127).trim()}...` : cleaned}` : (isComplete ? 'Checked access and loaded the source details I could use.' : 'I’m checking access and loading the source details I can use.');
    }
    if (/search|retriev|candidate|chunk|vector|hybrid|title|document intelligence/.test(normalized)) {
        return cleaned ? `${isComplete ? 'Searched evidence' : 'I’m searching evidence'}: ${cleaned.length > 130 ? `${cleaned.slice(0, 127).trim()}...` : cleaned}` : (isComplete ? 'Searched CaseDB evidence and narrowed it to useful source material.' : 'I’m searching CaseDB evidence and narrowing it to the useful source material.');
    }
    if (/timeline|chronolog|feed update|activity|field update/.test(normalized)) {
        return cleaned ? `${isComplete ? 'Built the timeline' : 'I’m building the timeline'}: ${cleaned.length > 130 ? `${cleaned.slice(0, 127).trim()}...` : cleaned}` : (isComplete ? 'Built the timeline from case facts, activity, fields, and documents.' : 'I’m building the timeline from case facts, activity, fields, and documents.');
    }
    if (/draft|demand|letter|document|docx|template/.test(normalized)) {
        return cleaned ? `${isComplete ? 'Drafted from the available material' : 'I’m drafting from the available material'}: ${cleaned.length > 130 ? `${cleaned.slice(0, 127).trim()}...` : cleaned}` : (isComplete ? 'Drafted from the available case facts and source material.' : 'I’m drafting from the available case facts and source material.');
    }
    if (/calendar|schedule|deadline|order/.test(normalized)) {
        return cleaned ? `${isComplete ? 'Extracted scheduling details' : 'I’m extracting scheduling details'}: ${cleaned.length > 130 ? `${cleaned.slice(0, 127).trim()}...` : cleaned}` : (isComplete ? 'Extracted dates and scheduling rules for calendar-ready drafts.' : 'I’m extracting dates and scheduling rules for calendar-ready drafts.');
    }
    if (/field|extract|custom|contact|policy|claim/.test(normalized)) {
        return cleaned ? `${isComplete ? 'Matched document facts to fields' : 'I’m matching document facts to fields'}: ${cleaned.length > 130 ? `${cleaned.slice(0, 127).trim()}...` : cleaned}` : (isComplete ? 'Matched document facts to CaseDB fields for review.' : 'I’m matching document facts to CaseDB fields for review.');
    }
    if (/citation|source|evidence|verify|support/.test(normalized)) {
        return cleaned ? `${isComplete ? 'Verified source support' : 'I’m verifying source support'}: ${cleaned.length > 130 ? `${cleaned.slice(0, 127).trim()}...` : cleaned}` : (isComplete ? 'Checked which sources support the answer.' : 'I’m checking which sources can support the answer.');
    }
    if (/answer|final|complete|deliver|response|summar/.test(normalized)) {
        return cleaned ? `${isComplete ? 'Prepared the response' : 'I’m preparing the response'}: ${cleaned.length > 130 ? `${cleaned.slice(0, 127).trim()}...` : cleaned}` : (isComplete ? 'Turned the findings into a clear response.' : 'I’m turning the findings into a clear response.');
    }

    if (!cleaned) return '';
    return cleaned.length > 140 ? `${cleaned.slice(0, 137).trim()}...` : cleaned;
};

const buildJaneThoughtTraceSteps = (steps = [], options = {}) => {
    const includePending = Boolean(options.includePending);
    const seen = new Set();
    return steps.flatMap((step, index) => {
        if (!includePending && normalizeJaneRunStepStatus(step.status, 'pending') === 'pending') return [];
        const text = normalizeJaneThoughtText(step, index);
        if (!text) return [];
        const detail = stringifyRagPlannerValue(step.detail || '');
        const meta = normalizeRagPlannerValues(step.meta || []).slice(0, 2);
        const detailText = [detail, ...meta]
            .filter(Boolean)
            .filter((value, valueIndex, values) => values.findIndex(candidate => candidate.toLowerCase() === value.toLowerCase()) === valueIndex)
            .join(' · ');
        const key = [text, detailText].join('|').toLowerCase();
        if (seen.has(key)) return [];
        seen.add(key);
        return [{ ...step, thoughtText: text, thoughtDetail: detailText, thoughtKind: getJaneThoughtKind(text, detailText) }];
    });
};

const getJaneThoughtKind = (text = '', detail = '') => {
    const value = `${text} ${detail}`.toLowerCase();
    if (/(legal draft workflow|drafting document|draft workflow|document type:|trial preparation brief|demand letter|brief|motion|pleading)/.test(value)) {
        return 'draft_workflow';
    }
    if (/(assembling|assemble|case, chat, and source context|case context|chat context|source context|case #\d+)/.test(value)) {
        return 'context';
    }
    if (/(supporting sources|sources that should influence|selected sources|source selection|evidence selection)/.test(value)) {
        return 'sources';
    }
    if (/(did not find a matching drafting template|standard drafting rules|no matching template|without a template)/.test(value)) {
        return 'template_missing';
    }
    if (/(using s3 draft template|draft template:|template:|matched drafting template|using template)/.test(value)) {
        return 'template_found';
    }
    if (/(permission|access check|access checks|auth|authorized|authorization|case access|source access|checking access|checking permissions)/.test(value)) {
        return 'permissions';
    }
    if (/(searching|search casedb|document search|case search|retrieval|retrieving|looking up|source search|found \d+|candidate source)/.test(value)) {
        return 'search';
    }
    if (/(structured case facts|custom fields|field updates|case facts|database|case data|field data|loaded fields|loading fields)/.test(value)) {
        return 'database';
    }
    if (/(calendar|deadline|scheduling order|calendar draft|calendar entries|date calculation|docket|hearing date)/.test(value)) {
        return 'calendar';
    }
    if (/(reading document|read document|document text|summarizing|summary|selected document|document review|file text|page text|ocr)/.test(value)) {
        return 'document_read';
    }
    if (/(verification|verify|source support|citation support|checking support|validated|validation|grounded|support pass|final check)/.test(value)) {
        return 'verification';
    }
    return 'default';
};

const getJaneThoughtIcon = (kind) => {
    if (kind === 'draft_workflow') return FilePen;
    if (kind === 'context') return Layers;
    if (kind === 'sources') return BookOpen;
    if (kind === 'template_missing') return FilePlus2;
    if (kind === 'template_found') return FileSearch;
    if (kind === 'permissions') return ShieldCheck;
    if (kind === 'search') return Search;
    if (kind === 'database') return Database;
    if (kind === 'calendar') return CalendarDays;
    if (kind === 'document_read') return FileText;
    if (kind === 'verification') return CheckCheck;
    if (kind === 'default') return CircleCheck;
    return null;
};

const renderJaneThoughtMarker = (step = {}, extraClassName = '') => {
    const Icon = getJaneThoughtIcon(step.thoughtKind);
    const className = [
        'library-rag-run-plan-marker',
        Icon ? 'has-icon' : '',
        step.thoughtKind ? `kind-${step.thoughtKind}` : '',
        extraClassName,
    ].filter(Boolean).join(' ');

    return (
        <span className={className} aria-hidden="true">
            {Icon ? <Icon size={16} strokeWidth={2.2} /> : null}
        </span>
    );
};

export const renderJaneRunPlan = (plan = null, options = {}) => {
    const normalizedPlan = normalizeJaneRunPlan(plan, [], options);
    if (!normalizedPlan) return null;
    const visibleSteps = options.compact ? normalizedPlan.steps.slice(-7) : normalizedPlan.steps;
    const isThoughtTrace = Boolean(options.thoughtTrace);
    const isCollapsed = Boolean(options.collapsed);
    const isCollapsible = Boolean(options.collapsible);
    const statusLabel = titleCaseRagTraceValue(normalizedPlan.status);
    const verification = normalizedPlan.verification || null;
    if (isThoughtTrace) {
        const showActiveThinking = Boolean(options.showActiveThinking) && normalizedPlan.status !== 'complete' && normalizedPlan.status !== 'failed';
        const activeThinkingLabel = stringifyRagPlannerValue(options.activeThinkingLabel) || RAG_THINKING_LABEL;
        const thoughtSteps = buildJaneThoughtTraceSteps(visibleSteps, {
            includePending: normalizedPlan.mode === 'case_trial_analysis',
        });
        if (thoughtSteps.length === 0 && !showActiveThinking) return null;
        const recentThoughtStep = [...thoughtSteps].reverse().find(step => ['running', 'failed'].includes(step.status))
            || [...thoughtSteps].reverse().find(step => step.status === 'complete')
            || thoughtSteps[thoughtSteps.length - 1]
            || null;
        const completedCaseAnalysisSummary = normalizedPlan.mode === 'case_trial_analysis' && normalizedPlan.status === 'complete'
            ? (stringifyRagPlannerValue(normalizedPlan.summary) || 'Case analysis is ready for attorney review.')
            : '';
        const recentThoughtText = completedCaseAnalysisSummary || stringifyRagPlannerValue(recentThoughtStep?.thoughtText) || activeThinkingLabel;
        const runningTimeText = stringifyRagPlannerValue(options.runningTimeText);
        const completedCount = thoughtSteps.filter((step) => step.status === 'complete').length;
        const summaryText = stringifyRagPlannerValue(options.summaryText)
            || (completedCount > 0
                ? `Response plan complete. ${completedCount} ${completedCount === 1 ? 'step' : 'steps'} finished.`
                : 'Response plan ready.');
        const collapsedSummaryText = normalizedPlan.status === 'complete'
            ? summaryText
            : (runningTimeText ? `${recentThoughtText} · ${runningTimeText}` : recentThoughtText);
        const summaryRow = isCollapsible ? (
            <button
                type="button"
                className={`library-rag-run-plan-summary${isCollapsed ? '' : ' expanded'}`}
                onClick={typeof options.onToggleCollapsed === 'function' ? options.onToggleCollapsed : undefined}
                aria-expanded={!isCollapsed}
            >
                {isCollapsed && recentThoughtStep
                    ? renderJaneThoughtMarker(recentThoughtStep)
                    : <CircleCheck size={16} aria-hidden="true" />}
                <span>{isCollapsed ? collapsedSummaryText : summaryText}</span>
                <div className='library-rag-run-plan-summary-chevron-cont'>
                    <ChevronDown size={16} className="library-rag-run-plan-summary-chevron" aria-hidden="true" />
                </div>
            </button>
        ) : null;
        if (isCollapsed) {
            return summaryRow || (
                <button
                    type="button"
                    className="library-rag-run-plan-summary"
                    onClick={typeof options.onToggleCollapsed === 'function' ? options.onToggleCollapsed : undefined}
                    aria-expanded={false}
                >
                    <CircleCheck size={16} aria-hidden="true" />
                    <span>{summaryText}</span>
                    <ChevronDown size={16} className="library-rag-run-plan-summary-chevron" aria-hidden="true" />
                </button>
            );
        }
        return (
            <>
                {summaryRow}
                <div className={`library-rag-run-plan thought-trace ${normalizedPlan.status}${isCollapsible && !isCollapsed ? ' expanded' : ''}`} aria-label="Jane thinking trace">
                    <div className="library-rag-run-plan-steps">
                        {thoughtSteps.map((step, stepIndex) => (
                            <div key={`jane-run-thought-${step.id || stepIndex}`} className={`library-rag-run-plan-step ${step.status}`}>
                                {renderJaneThoughtMarker(step)}
                                <div className="library-rag-run-plan-step-body">
                                    <div className="library-rag-run-plan-step-title">
                                        <span>{step.thoughtText}</span>
                                    </div>
                                    {step.thoughtDetail ? <p>{step.thoughtDetail}</p> : null}
                                </div>
                            </div>
                        ))}
                        {showActiveThinking ? (
                            <div className="library-rag-run-plan-step running thinking">
                                {renderJaneThoughtMarker({ thoughtKind: 'thinking' }, 'is-thinking-dot')}
                                <div className="library-rag-run-plan-step-body">
                                    <div className="library-rag-run-plan-thinking-inline">
                                        <RagThinkingIndicator label={activeThinkingLabel} />
                                    </div>
                                </div>
                            </div>
                        ) : null}
                    </div>
                </div>
            </>
        );
    }
    return (
        <div className={`library-rag-run-plan ${normalizedPlan.status}`} aria-label="Jane run plan">
            <div className="library-rag-run-plan-header">
                <div>
                    <span className="library-rag-run-plan-kicker">{normalizedPlan.mode ? titleCaseRagTraceValue(normalizedPlan.mode) : 'Jane plan'}</span>
                    <strong>{normalizedPlan.title}</strong>
                    {normalizedPlan.summary ? <p>{normalizedPlan.summary}</p> : null}
                </div>
                <span className={`library-rag-run-plan-status ${normalizedPlan.status}`}>{statusLabel}</span>
            </div>
            <div className="library-rag-run-plan-steps">
                {visibleSteps.map((step, stepIndex) => (
                    <div key={`jane-run-step-${step.id || stepIndex}`} className={`library-rag-run-plan-step ${step.status}`}>
                        <span className="library-rag-run-plan-marker" aria-hidden="true" />
                        <div className="library-rag-run-plan-step-body">
                            <div className="library-rag-run-plan-step-title">
                                <span>{step.label}</span>
                                <small>{titleCaseRagTraceValue(step.status)}</small>
                            </div>
                            {step.detail ? <p>{step.detail}</p> : null}
                            {Array.isArray(step.meta) && step.meta.length > 0 ? (
                                <div className="library-rag-run-plan-meta">
                                    {step.meta.slice(0, 4).map((value, valueIndex) => <span key={`jane-run-meta-${stepIndex}-${valueIndex}`}>{value}</span>)}
                                </div>
                            ) : null}
                        </div>
                    </div>
                ))}
                {normalizedPlan.steps.length > visibleSteps.length ? (
                    <div className="library-rag-run-plan-more">+{normalizedPlan.steps.length - visibleSteps.length} more steps in details</div>
                ) : null}
            </div>
            {verification ? (
                <div className="library-rag-run-plan-verification">
                    <span>Source verification</span>
                    <strong>{stringifyRagPlannerValue(verification.status || verification.summary || 'Available')}</strong>
                </div>
            ) : null}
        </div>
    );
};

export const renderRagPlanner = (plannerSteps = []) => {
    const normalizedPlannerSteps = normalizeRagPlannerSteps(plannerSteps);
    if (normalizedPlannerSteps.length === 0) return null;
    return (
        <div className="library-rag-planner" aria-label="Retrieval planner">
            {normalizedPlannerSteps.map((step, stepIndex) => (
                <div key={`rag-planner-${step.id || stepIndex}`} className={`library-rag-planner-step ${step.type || 'status'}`}>
                    <span className="library-rag-planner-dot" />
                    <div className="library-rag-planner-content">
                        {step.message && <span className="library-rag-planner-message">{step.message}</span>}
                        {Array.isArray(step.steps) && step.steps.length > 0 && (
                            <div className="library-rag-planner-groups">
                                {step.steps.map((group, groupIndex) => (
                                    <div key={`rag-planner-group-${stepIndex}-${groupIndex}`} className="library-rag-planner-group">
                                        {group.label && <span className="library-rag-planner-label">{group.label}</span>}
                                        {(group.values || []).length > 0 && <span className="library-rag-planner-values">{(group.values || []).join(', ')}</span>}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            ))}
        </div>
    );
};

export const titleCaseRagTraceValue = (value = '') => String(value || '')
    .replace(/[_:]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, match => match.toUpperCase());

export const buildRagTraceRows = (trace = {}) => {
    if (!trace || typeof trace !== 'object') return [];
    const rows = [];
    const context = trace.query_context || {};
    const evidence = trace.evidence || {};
    const model = trace.model || {};

    if (trace.route) rows.push(['Route', titleCaseRagTraceValue(trace.route)]);
    if (trace.route_reason) rows.push(['Why', titleCaseRagTraceValue(trace.route_reason)]);
    if (trace.prompt_template) rows.push(['Prompt', trace.prompt_template]);
    if (context.case_id) rows.push(['Case', `#${context.case_id}`]);
    if (Array.isArray(context.reference_file_ids) && context.reference_file_ids.length > 0) rows.push(['Files', context.reference_file_ids.map(id => `#${id}`).join(', ')]);
    if (context.selected_sources_only) rows.push(['Scope', 'Strict selected sources']);
    if (context.prior_chat_used || context.prior_chat_suppressed) rows.push(['Prior chat', context.prior_chat_used ? 'Included for context' : 'Suppressed for this request']);
    if (Array.isArray(trace.tools) && trace.tools.length > 0) rows.push(['Tools', trace.tools.map(titleCaseRagTraceValue).join(', ')]);
    if (evidence.retrieval_mode) rows.push(['Retrieval', titleCaseRagTraceValue(evidence.retrieval_mode)]);
    if (evidence.chunk_source) rows.push(['Evidence', titleCaseRagTraceValue(evidence.chunk_source)]);
    if (Number.isFinite(Number(evidence.candidate_hits)) || Number.isFinite(Number(evidence.citations_returned))) {
        rows.push(['Counts', `${Number(evidence.candidate_hits || 0).toLocaleString()} candidates · ${Number(evidence.citations_returned || 0).toLocaleString()} citations`]);
    }
    if (evidence.filename_title_search_forced || Number.isFinite(Number(evidence.db_filename_exact_hits))) {
        rows.push(['Title search', `${Number(evidence.filename_assist_hits || 0).toLocaleString()} assisted · ${Number(evidence.db_filename_exact_hits || 0).toLocaleString()} DB filename`]);
    }
    if (model.key) rows.push(['Model', String(model.key)]);
    return rows;
};

export const getJaneTurnContractFromIntelligence = (intelligence = null) => {
    const contract = intelligence?.plan?.jane_turn_contract;
    return contract && typeof contract === 'object' ? contract : null;
};

export const buildJaneTurnContractRows = (contract = null) => {
    if (!contract || typeof contract !== 'object') return [];
    const rows = [];
    const context = contract.context && typeof contract.context === 'object' ? contract.context : {};
    const missingContext = Array.isArray(contract.missing_context) ? contract.missing_context.filter(Boolean) : [];
    const scope = [
        context.has_case_id ? 'Case selected' : '',
        context.has_reference_files ? 'Reference files' : '',
        context.selected_sources_only ? 'Strict sources' : '',
        context.document_topic_query ? 'Document topic' : '',
        context.language_editing_request ? 'Language edit' : '',
        context.pasted_handoff_analysis_request ? 'Handoff analysis' : '',
    ].filter(Boolean);

    if (contract.decision_class) rows.push(['Decision', titleCaseRagTraceValue(contract.decision_class)]);
    if (contract.route_goal) rows.push(['Goal', titleCaseRagTraceValue(contract.route_goal)]);
    if (contract.runtime_route) rows.push(['Runtime', titleCaseRagTraceValue(contract.runtime_route)]);
    if (contract.guardrail) rows.push(['Guardrail', titleCaseRagTraceValue(contract.guardrail)]);
    if (scope.length > 0) rows.push(['Context', scope.join(' · ')]);
    if (missingContext.length > 0) rows.push(['Missing', missingContext.map(titleCaseRagTraceValue).join(', ')]);
    return rows;
};

const RAG_EXECUTION_DETAIL_FAMILIES = Object.freeze({
    route: 'route',
    runtime: 'route',
    goal: 'route',
    tools: 'tools',
    prompt: 'prompt',
    template: 'prompt',
    retrieval: 'evidence',
    evidence: 'evidence',
    case: 'case',
    files: 'files',
    'reference files': 'files',
    counts: 'counts',
    model: 'model',
});

const normalizeRagExecutionDetailLabel = (label = '') => String(label || '')
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, ' ');

const normalizeRagExecutionDetailText = (value = '') => String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[_.:/\\-]+/g, ' ')
    .replace(/[^a-z0-9#]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const buildRagExecutionDetailSignature = (label, value) => {
    const family = RAG_EXECUTION_DETAIL_FAMILIES[normalizeRagExecutionDetailLabel(label)];
    if (!family) return '';

    const rawValues = Array.isArray(value) ? normalizeRagPlannerValues(value) : normalizeRagPlannerValues([value]);
    const normalizedValues = (family === 'tools' || family === 'files'
        ? rawValues.flatMap(item => String(item || '').split(/[,·]/g))
        : rawValues
    )
        .map(normalizeRagExecutionDetailText)
        .filter(Boolean);
    if (normalizedValues.length === 0) return '';

    const comparableValues = (family === 'tools' || family === 'files')
        ? [...new Set(normalizedValues)].sort()
        : normalizedValues;
    return `${family}:${comparableValues.join('|')}`;
};

const filterDuplicateRagDetailRows = (rows = [], seenSignatures = new Set()) => rows.filter(([label, value]) => {
    const signature = buildRagExecutionDetailSignature(label, value);
    if (!signature) return true;
    if (seenSignatures.has(signature)) return false;
    seenSignatures.add(signature);
    return true;
});

export const buildDedupedRagResponseDetails = (plannerSteps = [], trace = null, turnContract = null) => {
    const seenSignatures = new Set();
    const normalizedPlannerSteps = normalizeRagPlannerSteps(plannerSteps).map(step => ({
        ...step,
        steps: (step.steps || []).filter(group => {
            const signature = buildRagExecutionDetailSignature(group?.label, group?.values || []);
            if (!signature) return true;
            if (seenSignatures.has(signature)) return false;
            seenSignatures.add(signature);
            return true;
        }),
    }));
    const turnContractRows = filterDuplicateRagDetailRows(buildJaneTurnContractRows(turnContract), seenSignatures);
    const traceRows = filterDuplicateRagDetailRows(
        trace && typeof trace === 'object' ? buildRagTraceRows(trace) : [],
        seenSignatures
    );

    return {
        plannerSteps: normalizedPlannerSteps,
        turnContractRows,
        traceRows,
    };
};

const getRagFileReadinessLabel = (status = {}) => {
    if (status.direct_parse_used) return 'Direct text used';
    const chunkCount = Number(status.chunk_count || 0);
    const extraction = String(status.extraction_status || '').trim();
    const embedding = String(status.embedding_status || '').trim();
    if (chunkCount > 0 && embedding.toLowerCase() === 'complete') return 'Search ready';
    if (chunkCount > 0) return 'Text extracted';
    if (extraction) return titleCaseRagTraceValue(extraction);
    return 'Status unknown';
};

export const hasRagResponseDetails = (plannerSteps = [], trace = null, turnContract = null) => {
    const {
        plannerSteps: normalizedPlannerSteps,
        turnContractRows,
        traceRows,
    } = buildDedupedRagResponseDetails(plannerSteps, trace, turnContract);
    if (normalizedPlannerSteps.length > 0) return true;
    if (turnContractRows.length > 0) return true;
    if (!trace || typeof trace !== 'object') return false;
    const readinessItems = Array.isArray(trace.file_readiness) ? trace.file_readiness.filter(Boolean) : [];
    return traceRows.length > 0 || readinessItems.length > 0;
};

export const renderRagResponseDetailsBreakdown = (plannerSteps = [], trace = null, turnContract = null) => {
    const {
        plannerSteps: normalizedPlannerSteps,
        turnContractRows,
        traceRows: rows,
    } = buildDedupedRagResponseDetails(plannerSteps, trace, turnContract);
    const readinessItems = trace && typeof trace === 'object' && Array.isArray(trace.file_readiness) ? trace.file_readiness.filter(Boolean) : [];
    if (normalizedPlannerSteps.length === 0 && turnContractRows.length === 0 && rows.length === 0 && readinessItems.length === 0) return null;

    return (
        <div className="library-rag-response-details-breakdown">
            {normalizedPlannerSteps.length > 0 && renderRagPlanner(normalizedPlannerSteps)}
            {turnContractRows.length > 0 && (
                <div className="library-rag-turn-contract-card" aria-label="Jane runtime decision">
                    <span className="library-rag-response-details-section-title">Runtime decision</span>
                    <div className="library-rag-response-details-grid compact">
                        {turnContractRows.map(([label, value]) => (
                            <React.Fragment key={`jane-turn-contract-${label}-${value}`}>
                                <span className="library-rag-response-details-label">{label}</span>
                                <span className="library-rag-response-details-value">{value}</span>
                            </React.Fragment>
                        ))}
                    </div>
                </div>
            )}
            {rows.length > 0 && (
                <div className="library-rag-response-details-grid">
                    {rows.map(([label, value]) => (
                        <React.Fragment key={`rag-trace-${label}-${value}`}>
                            <span className="library-rag-response-details-label">{label}</span>
                            <span className="library-rag-response-details-value">{value}</span>
                        </React.Fragment>
                    ))}
                </div>
            )}
            {readinessItems.length > 0 && (
                <div className="library-rag-response-details-readiness">
                    <span className="library-rag-response-details-section-title">Attached file readiness</span>
                    {readinessItems.map((status, index) => (
                        <div key={`rag-readiness-${status.file_id || index}`} className="library-rag-response-details-readiness-item">
                            <span>{status.name || (status.file_id ? `File ${status.file_id}` : 'Attached file')}</span>
                            <strong>{getRagFileReadinessLabel(status)}</strong>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

const buildJaneRunInspectorRows = (inspector = null) => {
    if (!inspector || typeof inspector !== 'object') return [];
    const rows = [];
    const route = inspector.route || {};
    const context = inspector.context || {};
    const counts = inspector.counts || {};
    const tool = inspector.tool || {};
    const model = inspector.model || {};
    const background = inspector.background || {};
    const contract = route.registry_contract && typeof route.registry_contract === 'object' ? route.registry_contract : {};
    if (route.route || route.runtime_route) rows.push(['Route', titleCaseRagTraceValue(route.runtime_route || route.route)]);
    if (route.route_goal) rows.push(['Goal', titleCaseRagTraceValue(route.route_goal)]);
    if (route.retrieval_mode) rows.push(['Retrieval', titleCaseRagTraceValue(route.retrieval_mode)]);
    if (route.chunk_source) rows.push(['Evidence', titleCaseRagTraceValue(route.chunk_source)]);
    if (contract.source_policy) rows.push(['Source policy', titleCaseRagTraceValue(contract.source_policy)]);
    if (contract.write_policy) rows.push(['Write policy', titleCaseRagTraceValue(contract.write_policy)]);
    if (contract.confirmation_policy && contract.confirmation_policy !== 'none') rows.push(['Confirmation', titleCaseRagTraceValue(contract.confirmation_policy)]);
    if (contract.response_component) rows.push(['UI surface', titleCaseRagTraceValue(contract.response_component)]);
    if (contract.fallback_policy) rows.push(['Fallback', titleCaseRagTraceValue(contract.fallback_policy)]);
    if (context.case_id || context.active_case_id) rows.push(['Case', `Resolved #${context.case_id || 'none'} · Active #${context.active_case_id || 'none'}`]);
    if (Array.isArray(context.reference_file_ids) && context.reference_file_ids.length > 0) rows.push(['Reference files', context.reference_file_ids.map(id => `#${id}`).join(', ')]);
    rows.push(['Counts', `${Number(counts.candidate_hits || 0).toLocaleString()} candidates · ${Number(counts.citations || 0).toLocaleString()} citations · ${Number(counts.files || 0).toLocaleString()} files`]);
    if (Array.isArray(tool.tools) && tool.tools.length > 0) rows.push(['Tools', tool.tools.map(titleCaseRagTraceValue).join(', ')]);
    if (tool.confirmable_action_type) rows.push(['Action', `${titleCaseRagTraceValue(tool.confirmable_action_type)} · ${titleCaseRagTraceValue(tool.confirmable_action_status || tool.permission_state || 'pending')}`]);
    if (model.key || model.provider) rows.push(['Model', [model.provider, model.key].filter(Boolean).join(' · ')]);
    if (background.proactive_run_id || background.active_request?.status) {
        rows.push(['Background', [`run #${background.proactive_run_id || 'none'}`, background.active_request?.status].filter(Boolean).join(' · ')]);
    }
    return rows;
};

const RAG_REPEATED_INSPECTOR_EXECUTION_ROWS = new Set([
    'route',
    'goal',
    'retrieval',
    'evidence',
    'case',
    'reference files',
    'counts',
    'tools',
    'model',
]);

export const renderJaneRunInspector = (inspector = null, options = {}) => {
    if (!inspector || typeof inspector !== 'object') return null;
    const rows = buildJaneRunInspectorRows(inspector).filter(([label]) => (
        !options?.hideRepeatedExecutionDetails
        || !RAG_REPEATED_INSPECTOR_EXECUTION_ROWS.has(normalizeRagExecutionDetailLabel(label))
    ));
    const promptText = String(inspector.prompt?.text || '').trim();
    const warnings = Array.isArray(inspector.warnings) ? inspector.warnings.filter(Boolean) : [];
    const activeRequest = inspector.background?.active_request || null;
    const raw = inspector.raw && typeof inspector.raw === 'object' ? inspector.raw : null;

    return (
        <div className="library-rag-run-inspector" aria-label="Jane run inspector">
            <div className="library-rag-run-inspector-header">
                <span>Run inspector</span>
                <small>Saved execution metadata</small>
            </div>
            {promptText && (
                <div className="library-rag-run-inspector-prompt">
                    <span>Prompt</span>
                    <p>{promptText}</p>
                </div>
            )}
            {rows.length > 0 && (
                <div className="library-rag-run-inspector-list">
                    {rows.map(([label, value]) => (
                        <div className="library-rag-run-inspector-row" key={`jane-run-inspector-${label}-${value}`}>
                            <span>{label}</span>
                            <strong>{value}</strong>
                        </div>
                    ))}
                </div>
            )}
            {activeRequest && (
                <div className="library-rag-run-inspector-request">
                    <span>Latest request lock</span>
                    <div className="library-rag-run-inspector-list compact">
                        <div className="library-rag-run-inspector-row">
                            <span>Status</span>
                            <strong>{activeRequest.status || 'unknown'}</strong>
                        </div>
                        <div className="library-rag-run-inspector-row">
                            <span>Message</span>
                            <strong>{activeRequest.status_message || activeRequest.answer_preview || 'none'}</strong>
                        </div>
                    </div>
                </div>
            )}
            {warnings.length > 0 && (
                <div className="library-rag-run-inspector-warnings">
                    <span>Warnings</span>
                    {warnings.map((warning, index) => (
                        <div key={`jane-run-warning-${index}`} className="library-rag-run-inspector-warning">{warning}</div>
                    ))}
                </div>
            )}
            {raw && (
                <details className="library-rag-run-inspector-raw">
                    <summary>Raw route data</summary>
                    <pre>{JSON.stringify(raw, null, 2)}</pre>
                </details>
            )}
        </div>
    );
};
